<script>
import {Icon} from '@iconify/vue';

/* COMPOSANTS */
import AkLabel from '@components/v2/general/AkLabel';
import Dropdown from 'primevue/dropdown';

/* MIXINS */
import utilsMixin from '@mixins/utilsMixin';

export default {
  components: {Icon, AkLabel, Dropdown},
  mixins: [utilsMixin],
  inject: {pSubmitted: {default: null}, pDisabled: {default: null}, pIgnoredValidator: {default: null}},
  props: {
    inline: {
      type: Boolean,
      required: false,
      default: false,
    },
    label: {
      type: String,
      required: true,
    },
    modelValue: {
      required: true,
    },
    placeholder: {
      type: String,
      required: false,
      default: undefined,
    },
    className: {
      type: String,
      required: false,
      default: '',
    },
    inputClass: {
      type: String,
      required: false,
      default: '',
    },
    options: {
      type: Array,
      required: true,
    },
    optionLabel: {
      type: [String, Function],
      required: false,
      default: 'label',
    },
    optionValue: {
      type: [String, Function],
      required: false,
      default: 'value',
    },
    validator: {
      type: Object,
      required: false,
      default: undefined,
    },
    ignoredValidator: {
      type: Array,
      required: false,
      default: undefined,
    },
    submitted: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    required: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    editable: {
      type: Boolean,
      required: false,
      default: false,
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    optionDisabled: {
      type: [Boolean, String],
      required: false,
      default: 'false',
    },
    link: {
      type: Object,
      required: false,
      default: undefined,
    },
    action: {
      type: Object,
      required: false,
      default: undefined,
    },
    displayLabel: {
      type: Boolean,
      required: false,
      default: true,
    },
    defaultValue: {
      type: [Number, String],
      required: false,
      default: undefined,
    },
    filter: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['update:modelValue', 'value-change'],
  mounted() {
    if (!this.currentValue && this.defaultValue) {
      this.currentValue = this.defaultValue;
      if (this.defaultValue === '0') this.currentValue = 0;
    }
    const dropdown = this.$refs.dropdown;
    if (dropdown) {
      const original = dropdown.alignOverlay;
      dropdown.alignOverlay = function () {
        // appel méthode originale si besoin
        original.call(this);
        // puis override maxWidth
        if (this.overlay) {
          this.overlay.style.maxWidth = this.overlay.style.minWidth;
        }
      };
    }
  },
  computed: {
    currentValue: {
      get: function () {
        return this.modelValue;
      },
      set: function (val) {
        this.$emit('update:modelValue', val);
      },
    },
    optionList() {
      if (!this.options) return [];
      if (this.isRequired) {
        return this.options.filter(this.toFunction(this.optionValue));
      }
      return this.options;
    },
    isInline() {
      return this.inline;
    },
    getIgnoredValidator() {
      if (this.ignoredValidator != undefined) return this.ignoredValidator;
      if (this.pIgnoredValidator != undefined) return this.pIgnoredValidator;
      return [];
    },
    getPlaceholder() {
      return this.placeholder ? this.placeholder : this.label;
    },
    alreadySubmitted() {
      if (this.submitted !== undefined) return this.submitted;
      if (this.pSubmitted !== undefined) return this.pSubmitted;
      return false;
    },
    isDisabled() {
      if (this.disabled !== undefined) return this.disabled;
      if (this.pDisabled !== undefined) return this.pDisabled;
      return false;
    },
    hasValidator() {
      return this.validator !== undefined;
    },
    isInvalid() {
      if (this.hasValidator && this.getIgnoredValidator.length > 0) {
        if (this.getIgnoredValidator.$errors) {
          for (let error of this.getIgnoredValidator.$errors) {
            if (this.getIgnoredValidator.indexOf(error.$validator) === -1) {
              return true;
            }
          }
        }
      } else {
        return this.hasValidator && this.validator.$invalid;
      }
      return false;
    },
    isRequired() {
      return (
        this.hasValidator &&
        this.validator.required !== undefined &&
        (this.validator.required.$params === undefined ||
          this.validator.required.$params.type === 'required' ||
          (this.validator.required.$params.type === 'requiredIf' && this.validator.required.$params.prop === true))
      );
    },

    isRequiredFailed() {
      return (
        this.isRequired &&
        this.getIgnoredValidator.indexOf('required') === -1 &&
        (this.validator.required.$invalid || this.validator.$invalid) &&
        this.alreadySubmitted
      );
    },
  },
  methods: {
    valueChange(event) {
      this.$emit('value-change', event.value);
    },
  },
};
</script>

<template>
  <div class="w-full input-layout" :class="[className, {'form-group': !isInline, 'form-control': isInline}]">
    <AkLabel v-if="displayLabel && !isInline" :required="this.isRequired || this.required === true">
      {{ label }}
      <template v-if="link !== undefined && isDisabled" #link>
        <router-link :to="{name: link.route, params: link.params}" target="_blank">
          <Icon :icon="link.icon" class="text-secondary hover:text-grey-900" />
        </router-link>
      </template>
    </AkLabel>

    <Dropdown
      ref="dropdown"
      v-model="currentValue"
      class="w-full text-grey-900 font-inter form-control"
      :class="[inputClass, {'p-invalid': isInvalid && alreadySubmitted, disabled: isDisabled}]"
      :disabled="isDisabled"
      :editable="editable"
      :loading="loading"
      :option-disabled="optionDisabled"
      :option-label="optionLabel"
      :option-value="optionValue"
      :filter="filter"
      filter-placeholder="Filtrer"
      @change="valueChange"
      :options="optionList"
      :placeholder="getPlaceholder">
      <template #value="slotProps">
        <div v-if="slotProps.value" class="flex items-center">
          <div
            v-if="slotProps.value.icon"
            :class="`mr-2 fa ${slotProps.value.icon.toLowerCase()}`"
            style="width: 18px"></div>
          <div>{{ slotProps.value.label }}</div>
        </div>
        <span v-else>
          {{ slotProps.placeholder }}
        </span>
      </template>
      <template #option="slotProps">
        <div class="flex items-center">
          <div
            v-if="slotProps.option.icon"
            :class="`mr-2 fa ${slotProps.option.icon.toLowerCase()}`"
            style="width: 18px"></div>
          <div>{{ slotProps.option.label }}</div>
        </div>
      </template>
    </Dropdown>

    <span v-if="isDisabled && !isInline && action !== undefined" @click="this.action.perform()">
      <Icon :icon="action.icon" class="text-secondary hover:text-grey-900 icon-link" />
    </span>
    <div v-if="!isInline" class="flex flex-col flex-1">
      <small v-if="isRequiredFailed && !isInline" class="p-error">{{ $t('field_required', [label]) }}</small>
    </div>
  </div>
</template>
